// French and English (Studio spec §4): one language per user, never mixed on a screen.
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { announcePreference } from "../app/preference-events";
import en from "./en.json";
import fr from "./fr.json";

export const languages = ["en", "fr"] as const;
export type Language = (typeof languages)[number];

/** The local cache of the language for the next start; the saved preference wins over it (1d). */
export const LANGUAGE_KEY = "cs-lang";

const isLanguage = (value: string | null | undefined): value is Language =>
  languages.includes(value as Language);

/** The stored choice, else the browser's first French or English language, else English. */
export function detectLanguage(stored: string | null, browser: readonly string[]): Language {
  if (isLanguage(stored)) return stored;
  const preferred = browser.map((tag) => tag.slice(0, 2).toLowerCase()).find(isLanguage);
  return preferred ?? "en";
}

function readStored(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_KEY);
  } catch {
    return null;
  }
}

/** Starts i18next once, before the first render; `<html lang>` follows the language. */
export function initI18n(language?: Language) {
  const lng = language ?? detectLanguage(readStored(), navigator.languages ?? [navigator.language]);
  i18next.on("languageChanged", (current) => {
    document.documentElement.lang = current;
  });
  void i18next.use(initReactI18next).init({
    resources: { en: { translation: en }, fr: { translation: fr } },
    lng,
    fallbackLng: "en",
    supportedLngs: languages,
    interpolation: { escapeValue: false }, // React escapes text already
    returnNull: false,
    initAsync: false,
  });
  document.documentElement.lang = lng;
  return i18next;
}

/** Switches the whole interface and caches the language for the next start. */
export async function applyLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
  if (i18next.resolvedLanguage !== language) await i18next.changeLanguage(language);
}

/** The user's choice: applied at once and announced so it is saved. */
export async function setLanguage(language: Language): Promise<void> {
  announcePreference({ language });
  await applyLanguage(language);
}

export const currentLanguage = (): Language =>
  isLanguage(i18next.resolvedLanguage) ? i18next.resolvedLanguage : "en";
